import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, MapPin, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

function LocationRow({ location }) {
  return (
    <tr className="bg-gray-50/50 dark:bg-gray-700/20">
      <td colSpan="5" className="px-6 py-0">
        <div className="flex flex-wrap gap-4 py-2 text-xs text-gray-600 dark:text-gray-400">
          <span>Lokasi: <strong className="text-gray-900 dark:text-white">{location.code || location.name}</strong></span>
          {location.description && <span> - {location.description}</span>}
          <span className={`ml-auto ${location.is_active ? 'text-success-600' : 'text-danger-600'}`}>
            {location.is_active ? 'Aktif' : 'Nonaktif'}
          </span>
        </div>
      </td>
    </tr>
  );
}

function WarehouseRow({ warehouse, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button onClick={() => setExpanded(!expanded)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mr-2">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <span className="font-medium text-gray-900 dark:text-white">{warehouse.code}</span>
        </td>
        <td>
          <span className="text-sm text-gray-700 dark:text-gray-200">{warehouse.name}</span>
        </td>
        <td>
          <span className="text-sm text-gray-500 dark:text-gray-400">{warehouse.address || '-'}</span>
        </td>
        <td>
          <span className="text-sm text-gray-500 dark:text-gray-400">{warehouse.phone || '-'}</span>
        </td>
        <td>
          <span className={`badge ${warehouse.is_active ? 'badge-success' : 'badge-danger'}`}>
            {warehouse.is_active ? 'Aktif' : 'Nonaktif'}
          </span>
        </td>
        <td>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(warehouse)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
              <Edit2 className="h-4 w-4" />
            </button>
            <button onClick={() => onDelete(warehouse)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (warehouse.locations || []).length > 0 && (
        <>
          <tr className="bg-gray-50 dark:bg-gray-700/30">
            <td colSpan="6" className="px-6 py-2">
              <div className="flex items-center text-xs font-semibold text-gray-700 dark:text-gray-300">
                <MapPin className="h-3.5 w-3.5 mr-1 text-primary-500" />
                Lokasi / Sub-Gudang ({warehouse.locations.length})
              </div>
            </td>
          </tr>
          {warehouse.locations.map((loc) => (
            <LocationRow key={loc.id} location={loc} />
          ))}
        </>
      )}
      {expanded && (!warehouse.locations || warehouse.locations.length === 0) && (
        <tr>
          <td colSpan="6" className="px-6 py-3 text-center text-xs text-gray-400 dark:text-gray-500">
            Tidak ada sub-lokasi
          </td>
        </tr>
      )}
    </>
  );
}

export default function WarehousesPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', address: '', phone: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, [pagination.page, filters]);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/warehouses', { params });
      if (res.data.success) {
        setWarehouses(res.data.data?.warehouses || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/warehouses', form);
      if (res.data.success) {
        toast.success('Gudang berhasil dibuat');
        setShowCreateModal(false);
        setForm({ code: '', name: '', address: '', phone: '', is_active: true });
        fetchWarehouses();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/warehouses/${selectedWarehouse.id}`, form);
      if (res.data.success) {
        toast.success('Gudang berhasil diperbarui');
        setShowEditModal(false);
        setSelectedWarehouse(null);
        fetchWarehouses();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (warehouse) => {
    if (!confirm(`Yakin hapus gudang ${warehouse.name}?`)) return;
    try {
      const res = await api.delete(`/warehouses/${warehouse.id}`);
      if (res.data.success) {
        toast.success('Gudang berhasil dihapus');
        fetchWarehouses();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setForm({
      code: warehouse.code || '',
      name: warehouse.name || '',
      address: warehouse.address || '',
      phone: warehouse.phone || '',
      is_active: warehouse.is_active,
    });
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen Gudang</h1>
          <p className="page-subtitle">Kelola gudang dan lokasi penyimpanan</p>
        </div>
        <button onClick={() => { setForm({ code: '', name: '', address: '', phone: '', is_active: true }); setShowCreateModal(true); }} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Tambah Gudang
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari kode, nama..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchWarehouses()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchWarehouses} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : warehouses.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada gudang yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kode</th>
                    <th>Nama</th>
                    <th>Alamat</th>
                    <th>Telepon</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouses.map((w) => (
                    <WarehouseRow
                      key={w.id}
                      warehouse={w}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah Gudang Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Kode <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Contoh: WH-001" />
                </div>
                <div>
                  <label className="label">Nama <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama gudang" />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Alamat</label>
                  <textarea className="input min-h-[80px]" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Alamat lengkap gudang" rows={3} />
                </div>
                <div>
                  <label className="label">Telepon</label>
                  <input type="text" className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Nomor telepon gudang" />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWarehouse && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit Gudang</h3>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Kode <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Nama <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Alamat</label>
                  <textarea className="input min-h-[80px]" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} rows={3} />
                </div>
                <div>
                  <label className="label">Telepon</label>
                  <input type="text" className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedWarehouse(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}